import type { ProcessNode } from './graphTypes';

interface ThemeLikeColors {
  primary?: string;
  surfaceContainerHigh?: string;
}

interface ThemeLike {
  colors?: ThemeLikeColors;
}

/**
 * Resting (unselected) minimap dot colour. Matches the prototype's
 * `.ns-minimap` rendering (NodeShowcase.jsx:3064 — `'#676680'`):
 * a muted grey-purple that reads as a quiet wireframe-blueprint of the
 * canvas rather than competing with the actual node cards.
 *
 * Kept as a top-level constant so the test for default rendering can
 * reach for the same value without duplicating the literal.
 */
export const MINIMAP_NODE_COLOR_DEFAULT = '#676680';

/**
 * Per-type accent for the SELECTED node — mirrors the head-icon palette
 * (`.proc-node--input` / `--task` / `--gate` / `--output`) so the user can
 * still tell which kind of node they have selected from the minimap glance.
 *
 * Resting dots use the muted grey-purple (`MINIMAP_NODE_COLOR_DEFAULT`)
 * so the minimap reads cleanly per the prototype; only on selection does
 * the per-type accent appear.
 *
 * Notes use surface-container-high so they don't scream against the ruled
 * grid even when selected; the fallback is a muted theme grey if a consumer
 * mounts us with a partial theme (e.g. tests).
 */
export function minimapNodeColor(node: ProcessNode, theme?: ThemeLike): string {
  if (!node.selected) {
    return MINIMAP_NODE_COLOR_DEFAULT;
  }
  const fallback = theme?.colors?.surfaceContainerHigh ?? '#292932';
  switch (node.type) {
    case 'input':
      return '#C1C1FF';
    case 'task':
    case 'image_gen':
    case '3d_gen':
      return theme?.colors?.primary ?? '#C1C1FF';
    case 'gate':
    case 'manual':
      return '#FFDF9E';
    case 'output':
      return '#8CD88C';
    case 'note':
      return fallback;
    default:
      return fallback;
  }
}
